<template>
  <div>
    <div class="overlay" v-if="loading"></div>

    <div class="content_wrapper">
        <!-- Header -->
        <div class="header_section">
          <div class="header_texts">
            <h1 class="page_title">Register User</h1>
            <div class="role_badge">
              <span class="role_label">Current Role:</span>
              <span class="role_value">{{ currentRole }}</span>
            </div>
          </div>
        </div>

        <!-- Main Card -->
        <div class="premium_card">
          <form class="enroll_form" @submit.prevent="enroll">

            <!-- ============ STEP 1 · DEVICE ============ -->
            <div class="step_block">
              <div class="step_header">
                <span class="step_index">1</span>
                <div>
                  <h2 class="step_title">Find the device</h2>
                  <p class="step_desc">Enter the serial number — we'll look it up in Apple Business Manager.</p>
                </div>
              </div>

              <div class="form_group">
                <label class="input_label" for="serialnum">Serial Number</label>
                <div class="serial_input_wrap">
                  <input
                    type="text"
                    class="container_input"
                    :class="{ input_ok: serialStatus === 'found', input_err: serialStatus === 'notfound' }"
                    id="serialnum"
                    placeholder="EX. : FNYD6CYDCV"
                    style="text-transform: uppercase"
                    v-model="serialnum"
                    @input="handleSerialInput"
                    autocomplete="off"
                  />
                  <span class="status_chip checking" v-if="serialStatus === 'checking'">
                    <i class="bx bx-loader-alt bx-spin"></i> Checking ABM…
                  </span>
                  <span class="status_chip ok" v-else-if="serialStatus === 'found'">
                    <i class="bx bx-check-circle"></i> Found in ABM
                  </span>
                  <span class="status_chip err" v-else-if="serialStatus === 'notfound'">
                    <i class="bx bx-x-circle"></i> Not in ABM
                  </span>
                </div>
                <p class="hint_err" v-if="serialStatus === 'notfound'">
                  This serial number isn't in your Apple Business Manager inventory. Check it and try again.
                </p>
              </div>

              <!-- Device summary card (revealed when found) -->
              <transition name="reveal">
                <div class="device_card" v-if="serialStatus === 'found'">
                  <div class="device_card_head">
                    <i class="bx bx-mobile-alt device_card_icon"></i>
                    <div class="device_card_meta">
                      <span class="device_card_model">{{ devicemodel }}</span>
                      <span class="device_card_brand">{{ devicetype || 'Apple' }}</span>
                    </div>
                  </div>
                  <div class="form_group device_card_storage">
                    <label class="input_label" for="storage_selection">Storage Capacity</label>
                    <select class="container_input" id="storage_selection" v-model="devicestorage" required>
                      <option value="" disabled selected>Select storage capacity</option>
                      <option value="16GB">16GB</option>
                      <option value="32GB">32GB</option>
                      <option value="64GB">64GB</option>
                      <option value="128GB">128GB</option>
                      <option value="256GB">256GB</option>
                      <option value="512GB">512GB</option>
                      <option value="1TB">1TB</option>
                      <option value="2TB">2TB</option>
                      <option value="Other">Other (Specify…)</option>
                    </select>
                    <input
                      v-if="devicestorage === 'Other'"
                      type="text"
                      class="container_input animate_fade_in"
                      style="margin-top: 8px"
                      placeholder="e.g. 8GB or 4TB"
                      v-model="customStorage"
                    />
                  </div>
                </div>
              </transition>
            </div>

            <!-- ============ STEP 2 · CUSTOMER (revealed when found) ============ -->
            <transition name="reveal">
              <div class="step_block" v-if="serialStatus === 'found'">
                <div class="step_divider"></div>
                <div class="step_header">
                  <span class="step_index">2</span>
                  <div>
                    <h2 class="step_title">Customer details</h2>
                    <p class="step_desc">Fill in the customer being enrolled to this device.</p>
                  </div>
                </div>

                <div class="form_row">
                  <div class="form_group">
                    <label class="input_label" for="fullname">Full Name</label>
                    <input type="text" class="container_input" id="fullname"
                      placeholder="Ex. : John Doe bin Adam" v-model="fullname" required />
                  </div>
                  <div class="form_group">
                    <label class="input_label" for="msisdn">MSISDN Number</label>
                    <input type="tel" class="container_input" id="msisdn"
                      placeholder="Ex.: 60123456789" v-model="msisdn" @focus="addPhoneCode"
                      required pattern="[0-9]{9,18}"
                      title="Please input your MSISDN number ranging from 9-18 digits only" />
                  </div>
                </div>

                <div class="form_row">
                  <div class="form_group">
                    <label class="input_label" for="nric">NRIC / Passport No.</label>
                    <input type="text" class="container_input" id="nric"
                      placeholder="Ex. : 090998016920 / C4339088 ( no spaces )" v-model="nric"
                      required pattern="^[0-9]{12}$|^[A-Za-z0-9]+$"
                      title="12-digit IC or alphanumeric passport number" />
                  </div>
                  <div class="form_group">
                    <label class="input_label" for="email">Email</label>
                    <input type="email" class="container_input" id="email"
                      placeholder="Ex. : john_doe@secondlifeasia.com" v-model="email" required />
                  </div>
                </div>

                <div class="form_group form_group_half">
                  <label class="input_label" for="contracttenure">Contract Tenure (months)</label>
                  <input type="tel" class="container_input" id="contracttenure"
                    placeholder="Ex. : 12 ( for 12 months )" v-model="contracttenure"
                    required pattern="[0-9]{1,3}" title="Enter number of months" />
                </div>
              </div>
            </transition>

            <!-- Submit -->
            <div class="button_container">
              <button type="submit" class="primary_btn" :disabled="!canEnroll || isEnrolling">
                Enroll
              </button>
              <p class="enroll_hint" v-if="serialStatus !== 'found'">
                Enter a valid serial number to continue.
              </p>
            </div>
          </form>
        </div>
      </div>

    <!-- Enrolling loading -->
    <ModalBase
      v-if="isEnrolling"
      size="sm"
      title="Enrolling…"
      subtitle="Please wait while we register the device and enroll the user."
      icon="bx bx-loader-alt bx-spin"
      iconVariant="amber"
      :showClose="false"
    >
      <div class="premium-loader-container">
        <div class="premium-loader"></div>
      </div>
    </ModalBase>

    <!-- Success Modal -->
    <ModalBase
      v-if="success"
      size="sm"
      title="Enrollment Successful"
      subtitle="The device has been registered and the user has been enrolled."
      icon="bx bx-check-circle"
      iconVariant="success"
      @close="goManageDevice"
    >
      <template #footer>
        <CtrlButton variant="secondary" @click="registerAnother">Register Another</CtrlButton>
        <CtrlButton @click="goManageDevice">Manage Devices</CtrlButton>
      </template>
    </ModalBase>

    <!-- Error Modal -->
    <ModalBase
      v-if="errorMessage"
      size="sm"
      title="Enrollment Failed"
      :subtitle="errorMessage"
      icon="bx bx-error-circle"
      iconVariant="error"
      @close="clearError"
    >
      <template #footer>
        <CtrlButton variant="secondary" @click="clearError">Try Again</CtrlButton>
      </template>
    </ModalBase>

    <!-- Device-type unavailable (403) -->
    <ModalBase
      v-if="productFamily"
      size="sm"
      title="Feature Unavailable"
      :subtitle="featureErrorMsg"
      icon="bx bx-error-circle"
      iconVariant="warning"
      @close="productFamily = null"
    >
      <template #footer>
        <CtrlButton variant="secondary" @click="productFamily = null">Back</CtrlButton>
      </template>
    </ModalBase>

    <!-- Access Denied -->
    <ModalBase
      v-if="page_unavailable"
      size="sm"
      title="Access Denied"
      subtitle="You do not have permission to view this page."
      icon="bx bx-error-circle"
      iconVariant="error"
      @close="goDashboard"
    >
      <template #footer>
        <CtrlButton @click="goDashboard">Go to Devices</CtrlButton>
      </template>
    </ModalBase>

  </div>
</template>

<script>
import { useDebounceFn } from "@vueuse/core";
import { clientConfig } from "@/clientConfig";
import ModalBase from "@/components/ui/ModalBase.vue";
import CtrlButton from "@/components/ui/CtrlButton.vue";

export default {
  components: { ModalBase, CtrlButton },
  data() {
    return {
      loading: true,
      isEnrolling: false,
      success: false,
      page_unavailable: false,
      errorMessage: null,
      productFamily: null,
      featureErrorMsg: "",

      serialnum: "",
      serialStatus: "idle", // idle | checking | found | notfound
      devicemodel: "",
      devicetype: "",
      devicestorage: "",
      customStorage: "",

      fullname: "",
      msisdn: "",
      nric: "",
      email: "",
      contracttenure: "",

      currentRole: localStorage.role,
      preset_contact: clientConfig.preset_contact,

      debouncedSerialLookup: null,
    };
  },
  computed: {
    canEnroll() {
      return (
        this.serialStatus === "found" &&
        !!this.devicestorage &&
        (this.devicestorage !== "Other" || !!this.customStorage) &&
        !!this.fullname &&
        !!this.msisdn &&
        !!this.nric &&
        !!this.email &&
        !!this.contracttenure
      );
    },
  },
  methods: {
    handleSerialInput() {
      this.devicemodel = "";
      this.devicetype = "";
      if (!this.serialnum || this.serialnum.length < 4) {
        this.serialStatus = "idle";
        return;
      }
      this.serialStatus = "checking";
      this.debouncedSerialLookup();
    },

    lookupSerial() {
      if (!this.serialnum || this.serialnum.length < 4) {
        this.serialStatus = "idle";
        return;
      }
      const axios = require("axios");
      axios.request({
        method: "POST",
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/register/check-device",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          session_id: localStorage.token,
          serial_number: this.serialnum.toUpperCase(),
        }),
      }).then((res) => {
        if (res.status === 200 && res.data[0] && res.data[0].model) {
          this.devicemodel = res.data[0].model;
          this.devicetype = res.data[0].model_family || "";
          this.serialStatus = "found";
        } else {
          this.serialStatus = "notfound";
        }
      }).catch((err) => {
        if (err.response && err.response.status === 409) {
          this.serialStatus = "notfound";
          this.errorMessage = "This device is already enrolled in Ctrlytics.";
        } else {
          this.serialStatus = "notfound";
        }
      });
    },

    enroll() {
      if (this.isEnrolling || !this.canEnroll) return;
      this.isEnrolling = true;
      this.errorMessage = null;

      const storage = this.devicestorage === "Other" ? this.customStorage : this.devicestorage;

      const axios = require("axios");
      axios.request({
        method: "POST",
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/enroll",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          session_id: localStorage.token,
          serial_number: this.serialnum.toUpperCase(),
          Device_Model: this.devicemodel,
          storage: storage || "128GB",
          full_name: this.fullname,
          nric: this.nric,
          phone_no: this.msisdn,
          email: this.email,
          tenure: this.contracttenure,
        }),
      }).then((res) => {
        if (res.data.status === 200) {
          this.success = true;
        } else {
          this.errorMessage = res.data.message || "An unexpected error occurred.";
        }
      }).catch((err) => {
        if (err.response) {
          if (err.response.status === 403 && err.response.data.product_family) {
            this.productFamily = err.response.data.product_family;
            this.featureErrorMsg = err.response.data.message || "This device type is not supported on your plan.";
          } else if (err.response.status === 409) {
            this.errorMessage = "This device is already enrolled in Ctrlytics.";
          } else {
            this.errorMessage = err.response.data?.message || "Enrollment failed. Please try again.";
          }
        } else {
          this.errorMessage = "Network error. Please check your connection and try again.";
        }
      }).finally(() => {
        this.isEnrolling = false;
      });
    },

    addPhoneCode() {
      if (this.preset_contact && !this.msisdn.startsWith(this.preset_contact)) {
        this.msisdn = this.preset_contact + this.msisdn;
      }
    },

    clearError() {
      this.errorMessage = null;
    },

    checkLogin() {
      const axios = require("axios");
      axios.request({
        method: "POST",
        url: process.env.VUE_APP_BACKEND_URL + "/v1/login/validity-check",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ token: localStorage.token }),
      }).then((res) => {
        if (res.status === 200) {
          this.loading = false;
        } else {
          this.page_unavailable = true;
          this.loading = false;
        }
      }).catch(() => {
        this.page_unavailable = true;
        this.loading = false;
      });
    },

    goManageDevice() {
      this.$router.replace("/manage-devices");
    },
    goDashboard() {
      // Dashboard was removed for POS — fall back to Manage Devices.
      this.$router.replace("/manage-devices");
    },
    registerAnother() {
      // Reset the form for a fresh registration without leaving the page.
      this.success = false;
      this.serialnum = "";
      this.serialStatus = "idle";
      this.devicemodel = "";
      this.devicetype = "";
      this.devicestorage = "";
      this.customStorage = "";
      this.fullname = "";
      this.msisdn = "";
      this.nric = "";
      this.email = "";
      this.contracttenure = "";
    },
  },
  mounted() {
    this.checkLogin();
    this.debouncedSerialLookup = useDebounceFn(this.lookupSerial, 500);
  },
};
</script>

<style scoped>
.content_wrapper {
  margin: 0 auto;
  width: 100%;
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

/* Header */
.header_section { display: flex; align-items: flex-start; }
.header_texts {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  margin-top: 4px;
}
.page_title {
  font-family: var(--font-family-header);
  font-weight: 800;
  font-size: 32px;
  color: var(--text-main);
  margin: 0;
  line-height: 1.2;
}
.role_badge {
  background-color: var(--surface-color);
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  display: flex;
  gap: 8px;
  box-shadow: var(--shadow-sm);
  align-items: center;
}
.role_label { color: var(--text-muted); font-weight: 500; }
.role_value  { color: var(--text-main); font-weight: 700; }

/* Card */
.premium_card {
  width: 100%;
  background-color: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 40px;
  box-shadow: var(--shadow-md);
}

.enroll_form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  max-width: 760px;
  margin: 0 auto;
}

/* Step blocks */
.step_block { display: flex; flex-direction: column; gap: var(--spacing-lg); }

.step_divider {
  border-top: 1px solid var(--border-color);
  margin-bottom: var(--spacing-sm);
}

.step_header { display: flex; align-items: flex-start; gap: 14px; }

.step_index {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: var(--text-main);
  font-family: var(--font-family-header);
  font-weight: 800;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.step_title {
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 18px;
  color: var(--text-main);
  margin: 0;
}
.step_desc {
  font-family: var(--font-family-base);
  font-size: 13px;
  color: var(--text-muted);
  margin: 2px 0 0 0;
}

/* Form */
.form_row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.form_group { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.form_group_half { max-width: calc(50% - 10px); }

.input_label {
  font-family: var(--font-family-base);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-main);
  margin-left: 4px;
}

.container_input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-family: var(--font-family-base);
  font-size: 14px;
  color: var(--text-main);
  background-color: var(--background-color);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}
.container_input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255, 214, 0, 0.2);
  background-color: #fff;
}
.input_ok  { border-color: #34c759 !important; }
.input_err { border-color: #ff3b30 !important; }

select.container_input {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
}

/* Serial input + status chip */
.serial_input_wrap { position: relative; display: flex; align-items: center; }
.serial_input_wrap .container_input { padding-right: 140px; }

.status_chip {
  position: absolute;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  pointer-events: none;
}
.status_chip i { font-size: 14px; }
.status_chip.checking { color: #8a6d00; background: rgba(255, 214, 0, 0.18); }
.status_chip.ok       { color: #1a7f37; background: rgba(52, 199, 89, 0.15); }
.status_chip.err      { color: #c0241a; background: rgba(255, 59, 48, 0.12); }

.hint_err {
  font-size: 12px;
  color: var(--error-color);
  margin: 2px 0 0 4px;
}

/* Device summary card */
.device_card {
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 18px 20px;
  background: linear-gradient(135deg, rgba(255, 237, 75, 0.10) 0%, var(--background-color) 100%);
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.device_card_head { display: flex; align-items: center; gap: 14px; }
.device_card_icon {
  font-size: 34px;
  color: var(--text-main);
  background: var(--surface-color);
  border-radius: 12px;
  padding: 8px;
  box-shadow: var(--shadow-sm);
}
.device_card_meta { display: flex; flex-direction: column; }
.device_card_model {
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 16px;
  color: var(--text-main);
}
.device_card_brand { font-size: 13px; color: var(--text-muted); }
.device_card_storage { max-width: 280px; }

/* Buttons */
.button_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: var(--spacing-sm);
}
.primary_btn {
  background-color: var(--primary-color);
  color: var(--text-main);
  padding: 13px 56px;
  border: none;
  border-radius: 50px;
  font-family: var(--font-family-header);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);
  box-shadow: var(--shadow-sm);
  min-width: 180px;
}
.primary_btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
  background-color: #ffde33;
}
.primary_btn:disabled { opacity: 0.5; cursor: not-allowed; }
.enroll_hint { font-size: 12px; color: var(--text-muted); margin: 0; }

/* Reveal transition */
.reveal-enter-active { transition: all 0.32s cubic-bezier(0.22, 1, 0.36, 1); }
.reveal-leave-active { transition: all 0.18s ease; }
.reveal-enter-from { opacity: 0; transform: translateY(-12px); }
.reveal-leave-to   { opacity: 0; transform: translateY(-8px); }

.animate_fade_in { animation: fadeIn 0.2s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

/* Page-load overlay */
.overlay {
  position: fixed; top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.5);
  z-index: 9999;
}

@media (max-width: 768px) {
  .form_row { grid-template-columns: 1fr; }
  .form_group_half { max-width: 100%; }
  .premium_card { padding: 24px; }
}
</style>
